// RADAR OPERATIVO · INSTITUCIONAL — R02 (contrato radar-institucional-r02-1).
// Observa y ORGANIZA un centro: CENTRO -> HABITACIÓN -> CAMA -> RESIDENTE. No atiende: el caso se
// abre en Central con su ID real (deep-link ?caso= ya existente). Consume el read-model scoped
// GET /api/radar/institucional: el bloque `r02` (casos por ID, directorio de habitaciones/camas,
// contexto de cobertura y metadatos de lectura) y el bloque legado sólo como contexto de actividad.
// La capa global de alertas y el sonido NO se tocan ni se duplican.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, WifiOff, LayoutGrid, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PersonaDrawer from "@/components/persona/PersonaDrawer";
import SesionIdentidad from "@/components/radar/SesionIdentidad";
import ControlesVista from "@/components/radar/inst/ControlesVista";
import AtencionCentro from "@/components/radar/inst/AtencionCentro";
import HabitacionR02 from "@/components/radar/inst/HabitacionR02";
import CasoPanel from "@/components/radar/inst/CasoPanel";
import MosaicoEkh from "@/components/ekh/MosaicoEkh";
import useRadarPrefs from "@/hooks/useRadarPrefs";
import { proyectarR02, ordenarHabitaciones, esPendiente } from "@/lib/radar/instR02";

const REFRESCO_MS = 5000;
const LIMITE_BASE = 60;

// IND-VIS · indicador PRINCIPAL del centro (camas, casos pendientes, casos en atención).
// Mismo dato, mismas etiquetas y mismo color semántico que el chip anterior: sólo cambia la
// jerarquía visual (número grande sobre su etiqueta), tomando como referencia el radar residencial.
function IndicadorGrande({ n, label, corta, unidad, num, testId, title }) {
  const valor = n == null ? "—" : n;
  // Cero o dato no confirmado: azul institucional neutro. El color semántico exige valor positivo.
  const numTone = n ? (num || "text-[#012472]") : "text-[#012472]";
  return (
    <div data-testid={testId}
      title={n == null ? "Dato no confirmado en esta lectura." : (title || `${n} ${unidad}`)}
      className="flex min-w-0 flex-1 items-center border-l border-slate-200/70 pl-2.5 pr-1.5 first:border-l-0 first:pl-0 sm:flex-none sm:pl-5 sm:pr-3">
      <div className="min-w-0 leading-none">
        <p className={`text-2xl font-extrabold tabular-nums sm:text-3xl ${numTone}`}>{valor}</p>
        <p className="mt-1.5 truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-[11px]">
          <span className="sm:hidden">{corta || label}</span>
          <span className="hidden sm:inline">{label}</span>
        </p>
      </div>
    </div>
  );
}

function Indicador({ n, label, unidad, tone, testId, title }) {
  // P4-A · un dato que no se pudo confirmar se muestra como «—», nunca como cero.
  const valor = n == null ? "—" : n;
  // Contador en CERO (o sin confirmar): neutro. El color semántico exige un valor positivo.
  const neutro = !n;
  return (
    <span data-testid={testId} title={n == null ? "Dato no confirmado en esta lectura." : (title || `${n} ${unidad}`)}
      className={`inline-flex shrink-0 items-center ${neutro ? "gv2-chip gv2-chip-neutral" : tone}`}>
      <span className="tabular-nums">{valor}</span>
      <span className="font-semibold uppercase tracking-wide">{label}</span>
    </span>
  );
}

export default function RadarInstitucional({ sitioId, snapshot = null, demoFichas = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const esDemo = !!snapshot;
  const [snapRemoto, setSnap] = useState(null);
  const snap = snapshot || snapRemoto;
  const [fallo, setFallo] = useState(false);
  // H5 · un 403/404 es un veredicto de ALCANCE, no una caída transitoria: la consulta TERMINA
  // (no se reintenta en bucle) y la pantalla declara la falta de acceso.
  const [sinAcceso, setSinAcceso] = useState(null);
  const [cargando, setCargando] = useState(true);
  // P2 · PAGINACIÓN REAL con el cursor del contrato. `extra` acumula las páginas siguientes sin
  // duplicar alarma_id; el refresco periódico vuelve a la primera página CONSERVANDO lo ya cargado,
  // para no anunciar que desaparecieron casos ni perder el caso seleccionado.
  const [extra, setExtra] = useState([]);
  const [paginacion, setPaginacion] = useState({ cursor: null, hayMas: false });
  const [cargandoMas, setCargandoMas] = useState(false);
  const [selPersona, setSelPersona] = useState(null);
  const [casoSel, setCasoSel] = useState(null);
  const [q, setQ] = useState("");
  const [pagina, setPagina] = useState(0);
  const [editando, setEditando] = useState(false);
  const [plegado, setPlegado] = useState(false);
  const [revelada, setRevelada] = useState(null);
  const recibidoRef = useRef(0);
  const refHabitacion = useRef(null);
  const [tick, setTick] = useState(0);

  const cargar = useCallback(async () => {
    if (!sitioId) return;
    try {
      const { data } = await api.get("/radar/institucional", { params: { sitio_id: sitioId, limite: LIMITE_BASE } });
      setSnap(data);
      // El cursor de la PRIMERA página sólo manda mientras no se hayan cargado páginas siguientes.
      setPaginacion((prev) => (prev.cursor
        ? prev
        : { cursor: data?.r02?.lectura?.cursor_siguiente || null, hayMas: !!data?.r02?.lectura?.hay_mas }));
      recibidoRef.current = Date.now();
      setFallo(false);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403 || st === 404) setSinAcceso(st);
      setFallo(true);
    }
    finally { setCargando(false); }
  }, [sitioId]);

  const cargarMas = useCallback(async () => {
    if (!sitioId || !paginacion.cursor || cargandoMas) return;
    setCargandoMas(true);
    try {
      const { data } = await api.get("/radar/institucional", {
        params: { sitio_id: sitioId, limite: LIMITE_BASE, cursor: paginacion.cursor },
      });
      const nuevos = data?.r02?.casos || [];
      setExtra((prev) => {
        const vistos = new Set(prev.map((c) => c.alarma_id));
        return [...prev, ...nuevos.filter((c) => !vistos.has(c.alarma_id))];
      });
      setPaginacion({
        cursor: data?.r02?.lectura?.cursor_siguiente || null,
        hayMas: !!data?.r02?.lectura?.hay_mas,
      });
    } catch { /* la página siguiente no se pudo leer: lo ya cargado se conserva */ }
    finally { setCargandoMas(false); }
  }, [sitioId, paginacion.cursor, cargandoMas]);

  useEffect(() => {
    if (esDemo) { setCargando(false); return; }
    if (sinAcceso) return;   // H5 · consulta terminada: sin reintentos contra un 403/404.
    cargar();
    const t = setInterval(cargar, REFRESCO_MS);
    return () => clearInterval(t);
  }, [esDemo, cargar, sinAcceso]);
  useEffect(() => { const t = setInterval(() => setTick((v) => v + 1), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    setSelPersona(null); setCasoSel(null); setQ(""); setPagina(0); setRevelada(null);
    setExtra([]); setPaginacion({ cursor: null, hayMas: false }); setSinAcceso(null);
  }, [sitioId]);

  const ventanaMin = snap?.ventana_actividad_min || 30;
  // Reloj del SERVIDOR: hora autoritativa del snapshot + tiempo transcurrido localmente.
  const nowMs = useMemo(() => {
    if (esDemo || !snap?.ahora) return Date.now();
    return new Date(snap.ahora).getTime() + (Date.now() - recibidoRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap, tick]);

  // Casos acumulados = primera página + páginas siguientes ya cargadas, sin duplicar alarma_id.
  const casosAcumulados = useMemo(() => {
    const base = snap?.r02?.casos || [];
    if (extra.length === 0) return base;
    const vistos = new Set(base.map((c) => c.alarma_id));
    return [...base, ...extra.filter((c) => !vistos.has(c.alarma_id))];
  }, [snap, extra]);

  const vista = useMemo(
    () => (snap ? proyectarR02(snap, casosAcumulados) : null), [snap, casosAcumulados]);
  const idsAutorizados = useMemo(
    () => (vista?.habitaciones || []).filter((h) => h.zona_id).map((h) => h.zona_id), [vista]);
  const { prefs, set: setPrefs, alternarHabitacion, persistente } =
    useRadarPrefs(user?.id || user?.email || (esDemo ? "demo" : null), sitioId || "demo", idsAutorizados);

  const ql = q.trim().toLowerCase();
  // Conjunto ELEGIDO (vista personal) -> filtro de búsqueda -> orden -> página. Ninguna habitación
  // queda inaccesible: el resto siempre tiene su navegación explícita.
  const conjunto = useMemo(() => {
    let hs = vista?.habitaciones || [];
    if (prefs.usarMiVista && prefs.miVista.length > 0) {
      hs = hs.filter((h) => prefs.miVista.includes(h.zona_id));
    }
    if (ql) hs = hs.filter((h) => h.buscable.includes(ql));
    return ordenarHabitaciones(hs, prefs.orden);
  }, [vista, prefs.usarMiVista, prefs.miVista, prefs.orden, ql]);

  const porPagina = prefs.cantidad === "todas" ? conjunto.length || 1 : Number(prefs.cantidad);
  const paginas = Math.max(1, Math.ceil(conjunto.length / porPagina));
  const paginaSegura = Math.min(pagina, paginas - 1);
  const mostradas = conjunto.slice(paginaSegura * porPagina, paginaSegura * porPagina + porPagina);

  const irACentral = useCallback((caso) => {
    const aid = caso?.alarma_id || caso?.id;
    if (aid) navigate(`/consola-v3?caso=${encodeURIComponent(aid)}`);
  }, [navigate]);

  // ACCIÓN DELIBERADA: revela la habitación aunque esté oculta por búsqueda, selección personal,
  // página o sección plegada. No ocurre sola: sólo cuando la persona lo pide.
  const verHabitacion = useCallback((zonaId) => {
    if (!zonaId) return;
    setQ("");
    setPlegado(false);
    setRevelada(zonaId);
    const base = vista?.habitaciones || [];
    const dentroDeMiVista = prefs.usarMiVista && prefs.miVista.length > 0 && !prefs.miVista.includes(zonaId);
    if (dentroDeMiVista) setPrefs({ usarMiVista: false });
    const lista = ordenarHabitaciones(
      dentroDeMiVista || !prefs.usarMiVista || prefs.miVista.length === 0
        ? base
        : base.filter((h) => prefs.miVista.includes(h.zona_id)),
      prefs.orden,
    );
    const idx = lista.findIndex((h) => h.zona_id === zonaId);
    if (idx >= 0) setPagina(prefs.cantidad === "todas" ? 0 : Math.floor(idx / Number(prefs.cantidad)));
  }, [vista, prefs, setPrefs]);

  useEffect(() => {
    if (revelada && refHabitacion.current) {
      refHabitacion.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [revelada, paginaSegura]);

  const sitio = snap?.sitio || {};
  const resumenLegado = snap?.resumen || {};
  const resumenCasos = vista?.resumenCasos || null;
  const [resumenAbierto, setResumenAbierto] = useState(false);
  // CHIP DE PENDIENTES · refleja la prioridad MÁS ALTA efectivamente confirmada entre los casos que
  // contabiliza. Con cero casos es neutro, y con páginas sin leer o lectura no confirmada también:
  // un subconjunto parcial NO autoriza a deducir la prioridad del agregado.
  const tonoPendientes = useMemo(() => {
    if (!resumenCasos?.pendientes) return null;
    if (paginacion.hayMas || vista?.noConfirmado) return null;
    const pend = (vista?.casos || []).filter(esPendiente);
    const prio = (c) => (c.prioridad === "maxima" ? "alta" : c.prioridad);
    if (pend.some((c) => prio(c) === "alta")) return "text-[#FE6A55]";
    if (pend.some((c) => prio(c) === "media")) return "text-[#B45309]";
    return null;
  }, [resumenCasos, paginacion.hayMas, vista]);

  return (
    <div data-testid="radar-institucional-page" className="gv2 tema-noche-auto flex h-full min-h-0 flex-col">
      {/* CABECERA MÍNIMA FIJA (V2.1) — sólo identidad del centro, estado de la vista y sesión.
          Indicadores, filtros y búsqueda viajan CON el contenido: en pantalla estrecha las
          habitaciones aparecen pronto y casi todo el viewport se puede desplazar. */}
      <div className="gv2-header sticky top-0 z-20 shrink-0">
        <div className="flex items-center gap-2 px-3 py-2 sm:gap-3 sm:px-5">
          <span className="gv2-module-icon shrink-0">
            <LayoutGrid className="h-5 w-5" strokeWidth={1.9} />
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <h1 data-testid="radar-inst-titulo" className="truncate text-[15px] font-extrabold tracking-tight text-[#012472] md:text-lg">
              {sitio.nombre || "Radar Operativo"}
            </h1>
            <p data-testid="radar-inst-subtitulo" className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Institucional{sitio.ciudad ? ` · ${sitio.ciudad}` : ""}
            </p>
          </div>

          <span data-testid="radar-inst-estado-vista"
            title={!fallo
              ? "La vista se actualiza automáticamente. No implica que la cobertura de los sensores esté confirmada."
              : "La vista dejó de actualizarse. Lo que ves puede no reflejar la situación actual."}
            className={`gv2-chip inline-flex shrink-0 items-center ${!fallo ? "gv2-chip-live" : "gv2-chip-alert"}`}>
            {!fallo ? <Radio className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">{!fallo ? "EN VIVO" : "SIN ACTUALIZAR"}</span>
            <span className="sr-only sm:hidden">{!fallo ? "Vista en vivo" : "Vista sin actualizar"}</span>
          </span>

          <SesionIdentidad />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="gv2-dock-space mx-auto max-w-[1800px] space-y-3 p-3 sm:p-4">
          {cargando ? (
            <p className="p-10 text-center text-slate-400">Cargando centro…</p>
          ) : sinAcceso ? (
            <div data-testid="radar-inst-sin-acceso" className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-8 text-center text-sm text-amber-800">
              <p className="font-bold">No tienes acceso a este centro.</p>
              <p className="mt-1">
                Tu cuenta no está autorizada para monitorear este sitio{sinAcceso === 403 ? "" : " o el centro no existe"}.
                La consulta se detuvo: no hay estado que mostrar y no se reintenta.
              </p>
              <button type="button" data-testid="radar-inst-elegir-centro" onClick={() => navigate("/centro-v3")}
                className="mt-3 inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-amber-400 bg-white px-3 text-xs font-bold text-amber-800">
                <ArrowLeft className="h-3.5 w-3.5" /> Elegir otro centro
              </button>
            </div>
          ) : !snap ? (
            <p data-testid="radar-inst-error" className="rounded-xl border border-dashed border-indigo-200 bg-indigo-50 p-8 text-center text-sm text-indigo-700">
              No se pudo consultar este centro. Reintentando; no hay estado que mostrar.
            </p>
          ) : (
            <>
              <div data-testid="radar-inst-indicadores" className="gv2-panel flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-2 rounded-2xl px-3 py-2.5 sm:px-4">
                <div className="flex min-w-0 flex-1 items-center gap-1 sm:flex-none sm:gap-0">
                  <IndicadorGrande n={vista?.totalCamas ?? null} label="camas" unidad="camas registradas"
                    title="Camas registradas en este centro. No son personas ni equipos."
                    testId="radar-inst-ind-camas" />
                  <IndicadorGrande n={resumenCasos ? resumenCasos.pendientes : null}
                    label="casos pendientes" corta="pend." unidad="casos sin tomar"
                    title="CASOS abiertos que nadie ha tomado. Un residente puede tener más de uno."
                    num={tonoPendientes} testId="radar-inst-ind-pendientes" />
                  <IndicadorGrande n={resumenCasos ? resumenCasos.en_atencion : null}
                    label="casos en atención" corta="atención" unidad="casos tomados"
                    title="CASOS ya tomados y en curso."
                    num="text-[#0891A6]" testId="radar-inst-ind-atencion" />
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-1.5 sm:ml-auto">
                  <span data-testid="radar-inst-estado-vista-movil"
                    className={`gv2-chip hidden shrink-0 items-center ${!fallo ? "gv2-chip-live" : "gv2-chip-alert"}`}>
                    {!fallo ? <Radio className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
                    {!fallo ? "EN VIVO" : "SIN ACTUALIZAR"}
                  </span>
                  <Indicador n={vista?.totalHabitaciones ?? null} label="hab." unidad="habitaciones"
                    title="Habitaciones registradas en este centro."
                    tone="gv2-chip" testId="radar-inst-ind-habitaciones" />
                  <button type="button" data-testid="radar-inst-resumen-toggle" onClick={() => setResumenAbierto((v) => !v)}
                    aria-expanded={resumenAbierto}
                    className="gv2-chip-btn gv2-compact inline-flex shrink-0 items-center rounded-full">
                    {resumenAbierto ? <>Menos <ChevronUp className="h-3 w-3" /></> : <>Más <ChevronDown className="h-3 w-3" /></>}
                  </button>
                  {vista?.noConfirmado && (
                    <span data-testid="radar-inst-lectura-no-confirmada"
                      title="El ensamblado de la lectura del centro falló. Los totales no se pueden confirmar: esto no significa que no haya habitaciones, camas ni casos."
                      className="gv2-chip gv2-chip-alert inline-flex items-center">
                      Lectura del centro no confirmada
                    </span>
                  )}
                </div>
              </div>

              {resumenAbierto && (
                <div data-testid="radar-inst-resumen-ampliado" className="gv2-panel rounded-2xl px-3 py-2 text-[11px] text-slate-500">
                  <p>
                    <span className="font-bold text-slate-700">{resumenLegado.residentes ?? 0}</span> residentes en el padrón ·{" "}
                    <span className="font-bold text-slate-700">{resumenCasos?.total ?? "—"}</span> casos abiertos
                    ({resumenCasos?.humanos ?? "—"} de persona, {resumenCasos?.tecnicos ?? "—"} técnicos) ·{" "}
                    <span className="font-bold text-slate-700">{resumenLegado.coberturas_revisar ?? 0}</span> capacidades de
                    equipo a revisar · actividad reciente observada en{" "}
                    <span className="font-bold text-slate-700">{resumenLegado.actividad_reciente ?? 0}</span> residentes
                    (ventana de {ventanaMin} min; es contexto, no crea casos).
                  </p>
                  <p className="mt-1 text-slate-400">
                    Las unidades no son intercambiables: casos, personas, habitaciones, camas y equipos se cuentan por separado.
                  </p>
                  {vista?.noConfirmado && (
                    <p className="mt-1 font-semibold text-[#9B2C2C]">
                      La lectura del centro no se pudo confirmar en este ciclo: los totales de casos,
                      habitaciones y camas aparecen como «—». No son ceros.
                    </p>
                  )}
                </div>
              )}

              <ControlesVista prefs={prefs} onPref={setPrefs} q={q} onQ={(v) => { setQ(v); setPagina(0); }}
                habitaciones={vista.habitaciones} onAlternar={alternarHabitacion}
                editando={editando} onEditar={() => setEditando((v) => !v)}
                visibles={mostradas.filter((h) => h.zona_id).length} totalHabitaciones={vista.totalHabitaciones}
                agrupacionSinUbicacion={mostradas.some((h) => h.sinUbicacion)}
                persistente={persistente} />

              <AtencionCentro casos={vista.casos} resumen={resumenCasos} lectura={vista.lectura}
                nowMs={nowMs} onAbrirCaso={irACentral} onVerHabitacion={verHabitacion}
                onSeleccionar={(c) => setCasoSel(c)} seleccionadoId={casoSel?.alarma_id}
                onMasCasos={cargarMas} hayMas={paginacion.hayMas} cargandoMas={cargandoMas} />

              <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start">
                <section data-testid="radar-inst-habitaciones" className="min-w-0 flex-1 space-y-2">                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <button type="button" data-testid="radar-inst-plegar" onClick={() => setPlegado((v) => !v)}
                      aria-expanded={!plegado}
                      className="inline-flex min-h-9 items-center gap-1.5 text-[13px] font-extrabold uppercase tracking-wider text-[#012472]">
                      {plegado ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      Habitaciones del centro
                    </button>
                    {paginas > 1 && (
                      <div className="flex items-center gap-1.5">
                        <button type="button" data-testid="radar-inst-pagina-anterior" disabled={paginaSegura === 0}
                          onClick={() => setPagina((p) => Math.max(0, p - 1))}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 disabled:opacity-40">
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span data-testid="radar-inst-pagina" className="text-[11px] font-bold tabular-nums text-slate-500">
                          {paginaSegura + 1} / {paginas}
                        </span>
                        <button type="button" data-testid="radar-inst-pagina-siguiente" disabled={paginaSegura >= paginas - 1}
                          onClick={() => setPagina((p) => Math.min(paginas - 1, p + 1))}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 disabled:opacity-40">
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {!plegado && (
                    conjunto.length === 0 ? (
                      <p data-testid="radar-inst-sin-coincidencias" className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400">
                        {ql
                          ? `Sin coincidencias para “${q}”. Los casos abiertos del centro siguen arriba, completos.`
                          : prefs.usarMiVista
                            ? "Tu vista no tiene habitaciones elegidas todavía. Usa «Editar mi vista»."
                            : "Este centro no tiene habitaciones registradas."}
                      </p>
                    ) : (
                      <div className={`grid grid-cols-1 items-start gap-2.5 sm:grid-cols-2 xl:grid-cols-3 ${prefs.densidad === "compacta" ? "2xl:grid-cols-5" : "2xl:grid-cols-4"}`}>
                        {mostradas.map((h) => (
                          <HabitacionR02 key={h.zona_id || "sin-ubicacion"} h={h}
                            compacta={prefs.densidad === "compacta"}
                            resaltada={revelada === h.zona_id}
                            hayMas={!!paginacion.hayMas}
                            noConfirmado={!!vista.noConfirmado}
                            innerRef={revelada === h.zona_id ? refHabitacion : undefined}
                            onAbrirCaso={(c) => setCasoSel(c)} onFicha={(pid) => setSelPersona(pid)} />
                        ))}
                      </div>
                    )
                  )}

                  {!plegado && paginas > 1 && (
                    <p data-testid="radar-inst-resto" className="text-[11px] text-slate-400">
                      Quedan {conjunto.length - mostradas.length} habitaciones en otras páginas de este conjunto.
                      Ninguna queda inaccesible.
                    </p>
                  )}
                  {revelada && (
                    <button type="button" data-testid="radar-inst-volver-vista" onClick={() => setRevelada(null)}
                      className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-bold text-slate-600">
                      <ArrowLeft className="h-3.5 w-3.5" /> Quitar el resaltado y volver a mi vista
                    </button>
                  )}
                </section>

                {casoSel && (
                  <div className="w-full shrink-0 lg:sticky lg:top-2 lg:w-[22rem]">
                    <CasoPanel caso={casoSel} nowMs={nowMs} onCerrar={() => setCasoSel(null)}
                      onAbrirCentral={irACentral} onVerHabitacion={verHabitacion}
                      onFicha={(pid) => setSelPersona(pid)} />
                  </div>
                )}
              </div>
              <MosaicoEkh sitioId={sitioId} casos={vista.casos} onAbrirCaso={irACentral} />
            </>
          )}
        </div>
      </div>

      <PersonaDrawer open={!!selPersona} onClose={() => setSelPersona(null)}
        isDemo={!!demoFichas} demoFicha={demoFichas ? demoFichas[selPersona] || null : null}
        personaId={demoFichas ? null : selPersona} />
    </div>
  );
}
